import {
    Children,
    cloneElement,
    PropsWithChildren,
    ReactElement,
    useEffect,
    useState
} from "react";

import { css, keyframes } from "@compiled/react";
import { useIntl } from "react-intl";

import HipchatChevronDownIcon from "@atlaskit/icon/glyph/hipchat/chevron-down";
import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { Initializer } from "../../../../analytics/atl-analytics/initializer";
import {
    Logo,
    LogoBrand,
    ProductKeyTypes
} from "../../../../libraries/wpl/foundations/src/index";
import { isEqualUnlocalizedPathname, useLocation } from "../../../../utils";
import Button, { ButtonType } from "../../button/src";

export interface ProductNavBarProps {
    productHref: string; // link for logo
    productName: LogoBrand;
    showTextWithoutLogo?: boolean;
    showFreeHrefButton?: boolean;
    showContactSalesHrefButton?: boolean;
    freeHref?: string; // link for Get it free button
    ariaLabel?: string;
    dataProductKey: ProductKeyTypes; // product key label for Get it free button
    logoLinkWrapperWidth?: string;
    onClick?: () => void;
}

const navigationWrapperCss = css({
    position: "sticky",
    insetBlockStart: 0,
    zIndex: 3
});

const navigationBarWrapperCss = css({
    alignItems: "flex-start",
    background: "#ffffff",
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    boxShadow: `0 2px 10px ${"rgba(0, 0, 0, 0.25)"}`
});

const navigationBarCss = css({
    alignItems: "center",
    display: "flex",
    flex: 1,
    justifyContent: "space-between",
    [media.above.md]: {
        height: "60px",
        maxWidth: "1200px",
        padding: `0 ${token("space.200")}`
    },
    [media.only.sm]: {
        height: "56px",
        padding: `0 5px 0 ${token("space.200")}`,
        position: "relative",
        zIndex: 1
    },
    [media.below.sm]: {
        flexFlow: "row wrap",
        height: "auto",
        padding: `0 ${token("space.200")}`
    }
});

const logoAndLinksCss = css({
    display: "flex"
});

const keyframeChevron = keyframes({
    "0%": {
        transform: "rotate(0deg)"
    },
    "100%": {
        transform: "rotate(-180deg)"
    }
});

const removeKeyframeChevron = keyframes({
    "0%": {
        transform: "rotate(-180deg)"
    },
    "100%": {
        transform: "rotate(0deg)"
    }
});

const dropdownSlideInAnimation = keyframes({
    from: {
        maxHeight: "0px"
    },
    to: {
        maxHeight: "500px"
    }
});

const dropdownSlideOutAnimation = keyframes({
    from: {
        maxHeight: "500px"
    },
    to: {
        maxHeight: "0px",
        display: "none"
    }
});

const listItemCss = css({
    position: "relative"
});

const buttonWrapper = css({
    display: "flex",
    padding: "15px 0"
});

const linkAndButtonWrapper = css({
    display: "flex",
    alignItems: "center"
});

const buttonStyle = css({
    fontSize: "1rem",
    fontWeight: 500,
    height: "2rem",
    width: "auto",
    float: "right",
    transition: "background 250ms",
    textWrap: "nowrap",
    [media.below.sm]: {
        position: "relative",
        width: "auto",
        flexFlow: "row wrap",
        insetInlineEnd: "unset"
    }
});

const contactSalesButtonStyle = css({
    fontSize: "1rem",
    fontWeight: 500,
    height: "2rem",
    background: "#ffab00 !important",
    width: "auto",
    float: "right",
    transition: "background 250ms",
    textWrap: "nowrap",
    [media.below.sm]: {
        position: "relative",
        width: "auto",
        flexFlow: "row wrap",
        insetInlineEnd: "unset"
    }
});

// eslint-disable-next-line complexity
export const ProductNavBar = (props: PropsWithChildren<ProductNavBarProps>) => {
    const intl = useIntl();
    const isShowFreeHrefButton = props.showFreeHrefButton ?? true;
    const isShowContactSalesHrefButton =
        props.showContactSalesHrefButton ?? false;
    const showTextWithoutLogo = props.showTextWithoutLogo ?? false;

    const [activeDropdown, setActive] = useState<boolean | null>(null);

    const [buttonType, setButtonType] = useState<ButtonType>("secondary");

    const location = useLocation();

    useEffect(() => {
        window.addEventListener("scroll", handleScroll);
    }, []);

    useEffect(() => {
        Initializer.autoTrack();
    }, [activeDropdown, props.showFreeHrefButton]);

    const handleScroll = () => {
        setButtonType(window.scrollY > 56 ? "primary" : "secondary");
    };

    const getItFreeText = intl.formatMessage({
        id: "productNavBar.get-it-free-button",
        defaultMessage: "Get it free"
    });

    const getContactSalesText = intl.formatMessage({
        id: "productNavBar.contact-sales-button",
        defaultMessage: "Contact sales"
    });

    const dropdownCss = [
        activeDropdown === null &&
            css({
                display: "none",
                cursor: "pointer",
                [media.below.md]: {
                    backgroundColor: "rgba(245, 40, 145, 0)",
                    border: "none",
                    color: "#0052cc",
                    display: "block",
                    paddingBlockStart: token("space.075"),
                    svg: {
                        height: token("space.400"),
                        width: token("space.400")
                    }
                }
            }),
        activeDropdown &&
            css({
                svg: {
                    animation: `${keyframeChevron} 300ms ease`,
                    animationFillMode: "forwards"
                },
                display: "none",
                cursor: "pointer",
                [media.below.md]: {
                    backgroundColor: "rgba(245, 40, 145, 0)",
                    border: "none",
                    color: "#0052cc",
                    display: "block",
                    paddingBlockStart: token("space.075"),
                    svg: {
                        height: token("space.400"),
                        width: token("space.400")
                    }
                }
            }),
        !activeDropdown &&
            css({
                svg: {
                    animation: `${removeKeyframeChevron} 300ms ease`,
                    animationFillMode: "forwards"
                },
                display: "none",
                cursor: "pointer",
                [media.below.md]: {
                    backgroundColor: "rgba(245, 40, 145, 0)",
                    border: "none",
                    color: "#0052cc",
                    display: "block",
                    paddingBlockStart: token("space.075"),
                    svg: {
                        height: token("space.400"),
                        width: token("space.400")
                    }
                }
            })
    ];

    const mobileCategoriesWrapperCss = [
        activeDropdown === null &&
            css({
                flex: 1,
                borderColor: "rgb(9, 30, 66)",
                backgroundColor: "#f4f5f7",
                borderBlockStart: `1px solid ${"#c1c7d0"}`,
                borderBlockEnd: `1px solid ${"#dfe1e5"}`,
                boxShadow: `0 2px 10px ${"rgba(0, 0, 0, .25)"}`,
                maxHeight: "0px",
                overflowX: "hidden",
                overflowY: "auto",
                position: "absolute",
                width: "100%",
                ul: {
                    margin: 0,
                    padding: "15px 0px 15px 30px",
                    listStyleType: "none",
                    [media.below.md]: {
                        paddingInlineEnd: "15px"
                    }
                },
                [media.above.md]: {
                    display: "none"
                }
            }),
        activeDropdown &&
            css({
                animation: `${dropdownSlideInAnimation} 800ms ease forwards`,
                flex: 1,
                borderColor: "rgb(9, 30, 66)",
                backgroundColor: "#f4f5f7",
                borderBlockStart: `1px solid ${"#c1c7d0"}`,
                borderBlockEnd: `1px solid ${"#dfe1e5"}`,
                boxShadow: `0 2px 10px ${"rgba(0, 0, 0, .25)"}`,
                maxHeight: "0px",
                overflowX: "hidden",
                overflowY: "auto",
                position: "absolute",
                width: "100%",
                ul: {
                    margin: 0,
                    padding: "15px 0px 15px 30px",
                    listStyleType: "none",
                    [media.below.md]: {
                        paddingInlineEnd: "15px"
                    }
                },
                [media.above.md]: {
                    display: "none"
                }
            }),
        !activeDropdown &&
            css({
                animation: `${dropdownSlideOutAnimation} 600ms ease forwards`,
                flex: 1,
                borderColor: "rgb(9, 30, 66)",
                backgroundColor: "#f4f5f7",
                borderBlockStart: `1px solid ${"#c1c7d0"}`,
                borderBlockEnd: `1px solid ${"#dfe1e5"}`,
                boxShadow: `0 2px 10px ${"rgba(0, 0, 0, .25)"}`,
                maxHeight: "0px",
                overflowX: "hidden",
                overflowY: "auto",
                position: "absolute",
                width: "100%",
                ul: {
                    margin: 0,
                    padding: "15px 0px 15px 30px",
                    listStyleType: "none",
                    [media.below.md]: {
                        paddingInlineEnd: "15px"
                    }
                },
                [media.above.md]: {
                    display: "none"
                }
            })
    ];

    const renderSelectionItems = (isMobile: boolean) => {
        const categoriesWrapperCss = [
            !isMobile &&
                css({
                    display: "flex",
                    flex: 1,
                    flexDirection: "row",
                    listStyle: "none",
                    margin: `0 ${token("space.200")}`,
                    padding: 0,
                    [media.below.md]: {
                        display: "none"
                    }
                })
        ];
        return (
            <ul css={categoriesWrapperCss}>
                {props.children &&
                    Children.map(props.children, (child, index) =>
                        child ? (
                            <li key={index} css={listItemCss}>
                                {cloneElement(child as ReactElement, {
                                    isSelected:
                                        (child as ReactElement).props
                                            .isSelected ||
                                        isEqualUnlocalizedPathname(
                                            (child as ReactElement).props.href,
                                            location.pathname
                                        ),
                                    isMobile
                                })}
                            </li>
                        ) : null
                    )}
            </ul>
        );
    };

    const renderDropdownButton = () => {
        if (props.children) {
            return (
                <button
                    css={dropdownCss}
                    onClick={() => setActive(!activeDropdown)}
                >
                    <HipchatChevronDownIcon label="" />
                </button>
            );
        }
        return null;
    };

    const logoWrapperCss = [
        props.logoLinkWrapperWidth !== "" &&
            css({
                width: props.logoLinkWrapperWidth,
                alignItems: "center",
                display: "flex"
            }),
        props.logoLinkWrapperWidth === "" &&
            css({
                width: "157px",
                alignItems: "center",
                display: "flex"
            }),
        props.logoLinkWrapperWidth === undefined &&
            css({
                width: "157px",
                alignItems: "center",
                display: "flex"
            })
    ];

    const textWrapperCss = css({
        height: "50px",
        color: "#091e42",
        width: "110px",
        alignItems: "center",
        display: "flex",
        flexDirection: "row"
    });

    const textCss = css({
        fontSize: "1.4rem",
        fontWeight: 500
    });

    return (
        <div css={navigationWrapperCss}>
            <div css={navigationBarWrapperCss}>
                <div css={navigationBarCss} className="productNavBar">
                    <div css={logoAndLinksCss}>
                        {showTextWithoutLogo ? (
                            <a
                                css={textWrapperCss}
                                href={props.productHref}
                                aria-label={props.ariaLabel}
                            >
                                <span css={textCss}>{props.ariaLabel}</span>
                            </a>
                        ) : (
                            <a
                                css={logoWrapperCss}
                                href={props.productHref}
                                aria-label={props.ariaLabel}
                            >
                                <Logo
                                    brand={props.productName}
                                    type="logo"
                                    appearance="brand"
                                />
                            </a>
                        )}
                        {renderDropdownButton()}
                        {renderSelectionItems(false)}
                    </div>
                    <div css={linkAndButtonWrapper}>
                        {isShowFreeHrefButton && (
                            <div
                                css={buttonWrapper}
                                className="showFreeHrefButton"
                            >
                                <Button
                                    text={getItFreeText}
                                    css={buttonStyle}
                                    url={props.freeHref}
                                    type={buttonType}
                                    aria-label={getItFreeText}
                                    dataEventProps={{
                                        "data-event-container": "navbar",
                                        "data-event": "clicked",
                                        "data-event-component": "linkButton",
                                        "data-label": getItFreeText,
                                        "data-product-key": props.dataProductKey
                                    }}
                                    onClick={props.onClick}
                                />
                            </div>
                        )}
                        {isShowContactSalesHrefButton && (
                            <div className="showContactSalesHrefButton">
                                <Button
                                    text={getContactSalesText}
                                    css={contactSalesButtonStyle}
                                    color="yellow"
                                    url={props.freeHref}
                                    type={buttonType}
                                    aria-label={getContactSalesText}
                                    dataEventProps={{
                                        "data-event-container": "navbar",
                                        "data-event": "clicked",
                                        "data-event-component": "linkButton",
                                        "data-label": getContactSalesText,
                                        "data-product-key": props.dataProductKey
                                    }}
                                    onClick={props.onClick}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div
                css={mobileCategoriesWrapperCss}
                className="mobileCategoriesWrapper"
            >
                {renderSelectionItems(true)}
            </div>
        </div>
    );
};

export default ProductNavBar;
